import { useContext, useMemo } from "react";
import { indexBy, prop } from "ramda";
import { getComments } from "../requests";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import GuestbookContext from "../context/GuestbookContext";

const PAGE_SIZE = 50;

export const useComments = () => {
  const {guestbookKey} = useContext(GuestbookContext)
  const queryClient = useQueryClient()

  const {
    data: { pages },
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["comments", guestbookKey],
    queryFn: ({pageParam = 1}) => getComments({
      guestbookKey,
      page: pageParam,
      pageSize: PAGE_SIZE
    }),
    getNextPageParam: ({total}, allPages) => {
      const currentPage = allPages.length;
      const totalPages = Math.ceil(total / PAGE_SIZE);

      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    placeholderData: {pages: []},
    enabled: !!guestbookKey,
    staleTime: Infinity,
  })

  const data = useMemo(() => pages.flatMap(page => page.items), [pages])
  const pagesCount = pages.length || 0;

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: ["comments", guestbookKey],
    })
  }

  const byId = useMemo(() => indexBy(prop("id"), data), [data])
  const allIds = useMemo(() => data.map(prop("id")), [data])

  const addComment = (newComment) => {
    queryClient.setQueryData(["comments", guestbookKey], (oldData) => ({
      ...oldData,
      pages: oldData.pages.map((page, idx) => {
        if (idx > 0) return page;
        return {
          ...page,
          items: [newComment, ...page.items]
        }
      })
    }))
  }

  const mutateComment = (commentId, updateFn) => {
    queryClient.setQueryData(["comments", guestbookKey], (oldData) =>
      ({
        ...oldData,
        pages: oldData.pages.map((page) => 
          ({
            ...page,
            items: page.items.map((c) => (c.id === commentId ? updateFn(c) : c))
          })
        )
      })
    )
  }

  return {
    byId,
    allIds,
    addComment,
    isLoading,
    mutateComment,
    invalidate,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    pagesCount
  };
}

